import { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { Plus, Building, Layers, Home, CheckCircle, Clock, ChevronRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button, CardStats } from '@components/ui';
import { CardGridSkeleton } from '@components/skeleton';
import { mockBlocks, getBlocksByConstructionId } from '@mocks/bloklar';
import BlokModal from './BlokModal';
import clsx from 'clsx';


export default function Blocks() {
    const { selectedConstruction, showAllConstructions } = useSelector((state) => state.construction);

    const [modalOpen, setModalOpen] = useState(false);
    const [selectedBlock, setSelectedBlock] = useState(null);

    const blocks = showAllConstructions
        ? mockBlocks
        : getBlocksByConstructionId(selectedConstruction?.id);

    // Stats
    const totalBlocks = blocks.length;
    const totalApartments = blocks.reduce((sum, b) => sum + b.totalApartments, 0);
    const soldApartments = blocks.reduce((sum, b) => sum + b.soldApartments, 0);
    const emptyApartments = blocks.reduce((sum, b) => sum + b.emptyApartments, 0);

    const handleAdd = () => {
        setSelectedBlock(null);
        setModalOpen(true);
    };

    const handleEdit = (block) => {
        setSelectedBlock(block);
        setModalOpen(true);
    };

    const handleSubmit = (data) => {
        console.log('Blok kaydedildi:', data);
        if (selectedBlock) {
            toast.success(`${data.name} bloğu başarıyla güncellendi`);
        } else {
            toast.success(`${data.name} bloğu başarıyla eklendi`);
        }
    };

    // Block Card Component
    const BlockCard = ({ block }) => {
        const occupancy = Math.round(((block.soldApartments + block.reservedApartments) / block.totalApartments) * 100);
        const soldPct = Math.round((block.soldApartments / block.totalApartments) * 100);
        const reservedPct = Math.round((block.reservedApartments / block.totalApartments) * 100);

        return (
            <div
                onClick={() => handleEdit(block)}
                className={clsx(
                    'group bg-bg-secondary rounded-xl border border-border-light',
                    'hover:border-primary/30 hover:shadow-lg transition-all duration-200',
                    'cursor-pointer overflow-hidden'
                )}
            >
                {/* Header */}
                <div className="p-4 pb-3 border-b border-border-light">
                    <div className="flex items-start justify-between gap-3">
                        <div className="flex items-center gap-3 min-w-0">
                            <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
                                <Building size={20} className="text-primary" />
                            </div>
                            <div className="min-w-0">
                                <h3 className="font-semibold text-text-primary group-hover:text-primary transition-colors">
                                    {block.name}
                                </h3>
                                {showAllConstructions && (
                                    <p className="text-xs text-text-muted truncate">
                                        {block.constructionName}
                                    </p>
                                )}
                            </div>
                        </div>
                        <Link
                            to={`/daireler?block=${block.id}`}
                            className="p-2 rounded-lg text-text-muted hover:text-primary hover:bg-primary/10 transition-colors"
                            onClick={(e) => e.stopPropagation()}
                            title="Daireleri Gör"
                        >
                            <ChevronRight size={18} />
                        </Link>
                    </div>
                </div>

                {/* Content */}
                <div className="p-4 space-y-4">
                    {/* Quick Stats */}
                    <div className="grid grid-cols-3 gap-2 text-center">
                        <div className="p-2 bg-success/5 rounded-lg">
                            <p className="text-lg font-bold text-success">{block.soldApartments}</p>
                            <p className="text-2xs text-text-muted">Satılan</p>
                        </div>
                        <div className="p-2 bg-warning/5 rounded-lg">
                            <p className="text-lg font-bold text-warning">{block.reservedApartments}</p>
                            <p className="text-2xs text-text-muted">Rezerve</p>
                        </div>
                        <div className="p-2 bg-neutral-100 dark:bg-neutral-700/50 rounded-lg">
                            <p className="text-lg font-bold text-text-secondary">{block.emptyApartments}</p>
                            <p className="text-2xs text-text-muted">Boş</p>
                        </div>
                    </div>

                    {/* Info Row */}
                    <div className="flex items-center justify-between text-sm text-text-secondary">
                        <span className="flex items-center gap-1.5">
                            <Layers size={14} />
                            {block.totalFloors} Kat
                        </span>
                        <span className="flex items-center gap-1.5">
                            <Home size={14} />
                            {block.totalApartments} Daire
                        </span>
                    </div>

                    {/* Progress Bar */}
                    <div>
                        <div className="flex justify-between text-xs mb-1.5">
                            <span className="text-text-muted">Doluluk</span>
                            <span className="font-semibold text-text-primary">{occupancy}%</span>
                        </div>
                        <div className="h-2 bg-neutral-100 dark:bg-neutral-700 rounded-full overflow-hidden flex">
                            {/* Sold portion */}
                            <div
                                className="h-full bg-success transition-all"
                                style={{ width: `${soldPct}%` }}
                            />
                            {/* Reserved portion */}
                            <div
                                className="h-full bg-warning transition-all"
                                style={{ width: `${reservedPct}%` }}
                            />
                        </div>
                        <div className="flex items-center gap-3 mt-1.5 text-2xs text-text-muted">
                            <span className="flex items-center gap-1">
                                <span className="w-2 h-2 rounded-full bg-success" />
                                Satılan
                            </span>
                            <span className="flex items-center gap-1">
                                <span className="w-2 h-2 rounded-full bg-warning" />
                                Rezerve
                            </span>
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    // Demo: Simüle edilmiş yükleme gecikmesi (1.2s)
    const [isLoading, setIsLoading] = useState(true);
    useEffect(() => {
        const timer = setTimeout(() => setIsLoading(false), 1200);
        return () => clearTimeout(timer);
    }, []);
    if (isLoading) return <CardGridSkeleton variant="block" cardCount={6} columns={3} />;


    return (
        <div className="p-4 md:p-6 space-y-6">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div>
                    <h1 className="text-xl sm:text-2xl font-bold text-text-primary">Bloklar</h1>
                    <p className="text-text-tertiary text-sm mt-1">
                        {showAllConstructions
                            ? 'Tüm inşaatlara ait bloklar'
                            : `${selectedConstruction?.name} - Bloklar`}
                    </p>
                </div>
                <Button
                    variant="primary"
                    icon={<Plus size={18} />}
                    onClick={handleAdd}
                >
                    Blok Ekle
                </Button>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <CardStats
                    title="Toplam Blok"
                    value={totalBlocks}
                    icon={Building}
                    color="primary"
                    size="sm"
                />
                <CardStats
                    title="Toplam Daire"
                    value={totalApartments}
                    icon={Home}
                    color="tertiary"
                    size="sm"
                />
                <CardStats
                    title="Satılan"
                    value={soldApartments}
                    icon={CheckCircle}
                    color="success"
                    size="sm"
                />
                <CardStats
                    title="Boş"
                    value={emptyApartments}
                    icon={Clock}
                    color="warning"
                    size="sm"
                />
            </div>

            {/* Blocks Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {blocks.map((block) => (
                    <BlockCard key={block.id} block={block} />
                ))}
            </div>

            {blocks.length === 0 && (
                <div className="text-center py-16 text-text-tertiary">
                    <Building size={56} className="mx-auto mb-4 opacity-40" />
                    <p className="text-lg">Henüz blok tanımlanmamış.</p>
                    <p className="text-sm mt-1">Yeni blok ekleyerek başlayın.</p>
                </div>
            )}

            {/* Modal */}
            <BlokModal
                isOpen={modalOpen}
                onClose={() => setModalOpen(false)}
                onSubmit={handleSubmit}
                block={selectedBlock}
            />
        </div>
    );
}
